import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { useNavigate } from "react-router-dom";
import PayoutHistoryComponent from "./component";
import Sidebar from "../../../component/Navigation/Sidebar/Sidebar";
import moment from 'moment';

const PayoutHistoryContainer = (props) => {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [filteredUploads, setFilteredUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    id: null,
    filename: ""
  });
  const [successPopup, setSuccessPopup] = useState({
    show: false,
    message: ""
  });

  // Fetch payout history
  useEffect(() => {
    fetchPayoutHistory();
  }, []);

  const fetchPayoutHistory = () => {
    setLoading(true);
    props.getDataFromAPI(
      '/partner/api/v1/payout-df',
      "GET",
      undefined,
      (response) => {
        console.log("API Response:", response);
        
        let uploadsData = [];
        
        // Handle different response structures
        if (response?.data && Array.isArray(response.data)) {
          uploadsData = response.data;
        } else if (response?.uploads && Array.isArray(response.uploads)) {
          uploadsData = response.uploads;
        } else if (Array.isArray(response)) {
          uploadsData = response;
        } else if (response?.status?.success && response?.data) {
          uploadsData = response.data;
        }
        
        // Ensure each item has an id and is properly formatted
        uploadsData = uploadsData.map((item, index) => ({
          ...item,
          id: item?.id || item?.payoutId || item?._id || `temp-${index}`,
          filename: item?.filename || item?.name || item?.file || 'N/A',
          status: item?.status || 'SETTLED',
          payoutDate: item?.payoutDate || item?.date || item?.createdAt || new Date().toISOString()
        }));
        
        console.log("Processed Uploads Data:", uploadsData);
        setUploads(uploadsData);
        setFilteredUploads(uploadsData);
        setLoading(false);
      },
      (error) => {
        console.error("Fetch error:", error);
        setError("Failed to fetch payout history");
        setLoading(false);
      }
    );
  };

  // Filter data with search and date
  useEffect(() => {
    filterData();
  }, [searchTerm, startDate, endDate, uploads]);

  const filterData = () => {
    let filtered = [...uploads];

    // Search by ID or filename
    if (searchTerm && searchTerm.trim()) {
      const trimmedSearchTerm = searchTerm.trim().toLowerCase();
      
      filtered = filtered.filter(item => {
        const itemId = item?.id?.toString() || '';
        const itemFilename = item?.filename?.toString() || '';
        const itemName = item?.name?.toString() || '';
        const itemFile = item?.file?.toString() || '';
        
        return itemId.toLowerCase().includes(trimmedSearchTerm) ||
               itemFilename.toLowerCase().includes(trimmedSearchTerm) ||
               itemName.toLowerCase().includes(trimmedSearchTerm) ||
               itemFile.toLowerCase().includes(trimmedSearchTerm);
      });
    }

    // Filter by date range
    if (startDate && endDate) {
      filtered = filtered.filter(item => {
        const dateVal = item?.payoutDate || item?.date || item?.createdAt;
        if (!dateVal) return false;
        const itemDate = moment(dateVal);
        return itemDate.isValid() && itemDate.isBetween(moment(startDate), moment(endDate), 'day', '[]');
      });
    } else if (startDate) {
      filtered = filtered.filter(item => {
        const dateVal = item?.payoutDate || item?.date || item?.createdAt;
        if (!dateVal) return false;
        const itemDate = moment(dateVal);
        return itemDate.isValid() && itemDate.isSameOrAfter(moment(startDate), 'day');
      });
    } else if (endDate) {
      filtered = filtered.filter(item => {
        const dateVal = item?.payoutDate || item?.date || item?.createdAt;
        if (!dateVal) return false;
        const itemDate = moment(dateVal);
        return itemDate.isValid() && itemDate.isSameOrBefore(moment(endDate), 'day');
      });
    }

    setFilteredUploads(filtered);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleStartDateChange = (e) => {
    const value = e?.target?.value || '';
    setStartDate(value);
  };

  const handleEndDateChange = (e) => {
    const value = e?.target?.value || '';
    setEndDate(value);
  };

  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    setShowDateFilter(false);
  };

  const handleUploadNew = () => {
    navigate("/home/dfpayout/dfpayoutcreate");
  };

  // Delete handlers
  const handleDeleteClick = (id, filename) => {
    setDeleteModal({
      show: true,
      id,
      filename: filename || 'N/A'
    });
  };

  const handleDeleteConfirm = () => {
    const { id } = deleteModal;
    if (!id) {
      setDeleteModal({ show: false, id: null, filename: "" });
      setError("Invalid record ID");
      return;
    }
    
    props.getDataFromAPI(
      `/partner/api/v1/payout-df/${id}`,
      "DELETE",
      null,
      (response) => {
        setDeleteModal({ show: false, id: null, filename: "" });
        setSuccessPopup({
          show: true,
          message: "Payout record deleted successfully!"
        });
        // Refresh the list
        fetchPayoutHistory();
      },
      (error) => {
        console.error("Delete error:", error);
        setError("Failed to delete payout record");
        setDeleteModal({ show: false, id: null, filename: "" });
      }
    );
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, id: null, filename: "" });
  };

  const handleSuccessPopupClose = () => {
    setSuccessPopup({ show: false, message: "" });
  };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <PayoutHistoryComponent
          uploads={filteredUploads}
          loading={loading}
          deleting={false}
          error={error}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          showDateFilter={showDateFilter}
          onToggleDateFilter={() => setShowDateFilter(!showDateFilter)}
          onClearDateFilter={clearDateFilter}
          onUploadNew={handleUploadNew}
          formatDisplayDate={formatDisplayDate}
          onDeleteClick={handleDeleteClick}
          deleteModal={deleteModal}
          onDeleteConfirm={handleDeleteConfirm}
          onDeleteCancel={handleDeleteCancel}
          successPopup={successPopup}
          onSuccessPopupClose={handleSuccessPopupClose}
        />
      </div>
    </div>
  );
};

// Format date for display
const formatDisplayDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = moment(dateString);
  if (!date.isValid()) return "N/A";
  return date.format(`DD MMM YYYY`);
};

function mapStateToProps(state) {
  return { state };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(PayoutHistoryContainer);